/**
 * Gets a relative time string based on the provided date
 * @param date - The date to compare against current time
 * @returns A string representing the relative time band
 */
export function getRelativeTime(date: Date | string | number): string {
  const inputDate = new Date(date);
  const now = new Date();

  // Reset time to start of day for calendar-based comparisons using local time
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfInputDay = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate()
  );

  const diffInMs = startOfToday.getTime() - startOfInputDay.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Handle future dates
  if (diffInDays < 0) {
    return 'Today';
  }

  // Today (same calendar day)
  if (diffInDays === 0) {
    return 'Today';
  }

  // Yesterday (previous calendar day)
  if (diffInDays === 1) {
    return 'Yesterday';
  }

  // 2-20 days
  if (diffInDays >= 2 && diffInDays <= 20) {
    return `${diffInDays}d`;
  }

  // 3-11 weeks (21-77 days)
  if (diffInDays >= 21 && diffInDays <= 77) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks}w`;
  }

  // 3-11 months (78-336 days)
  if (diffInDays >= 78 && diffInDays <= 336) {
    // Map 78-336 days to 3-11 months using explicit ranges
    if (diffInDays <= 107) return '3m';
    if (diffInDays <= 137) return '4m';
    if (diffInDays <= 167) return '5m';
    if (diffInDays <= 197) return '6m';
    if (diffInDays <= 227) return '7m';
    if (diffInDays <= 257) return '8m';
    if (diffInDays <= 287) return '9m';
    if (diffInDays <= 317) return '10m';
    return '11m'; // 318-336 days
  }

  // 1 year or more (337+ days)
  return '1y';
}
